/**
 * Facebook Pixel Tracking Service
 * Centraliza todo rastreamento de eventos da Meta para análise de conversão
 */

interface FacebookPixelData {
  [key: string]: string | number | boolean | string[] | Record<string, any>;
}

class FacebookPixelService {
  private pixelId = '857151420812858';
  private isInitialized = false;

  constructor() {
    this.initPixel();
  }

  /**
   * Inicializa o Facebook Pixel
   */
  private initPixel(): void {
    if (this.isInitialized) return;

    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('init', this.pixelId);
      this.isInitialized = true;
    }
  }

  /**
   * Rastreia PageView - Evento básico para todas as páginas
   */
  trackPageView(pageTitle?: string, pagePath?: string): void {
    if (!this.isPixelLoaded()) return;

    const data: FacebookPixelData = {};
    if (pageTitle) data.page_title = pageTitle;
    if (pagePath) data.page_path = pagePath;

    (window as any).fbq('track', 'PageView', data);
  }

  /**
   * Rastreia visualização de conteúdo (série, plano, etc)
   */
  trackViewContent(
    contentName: string,
    contentIds: string[],
    value?: number,
    category?: string,
    contentType: string = 'product'
  ): void {
    if (!this.isPixelLoaded()) return;

    const data: FacebookPixelData = {
      content_name: contentName,
      content_ids: contentIds,
      content_type: contentType,
      content_category: category || 'product',
    };

    if (value !== undefined) {
      data.value = value;
      data.currency = 'BRL';
    }

    (window as any).fbq('track', 'ViewContent', data);
  }

  /**
   * Rastreia início de checkout (clique em plano)
   */
  trackInitiateCheckout(
    contentName: string,
    value: number,
    planId?: string,
    numItems: number = 1
  ): void {
    if (!this.isPixelLoaded()) return;

    const data: FacebookPixelData = {
      content_name: contentName,
      content_ids: planId ? [planId] : [],
      value,
      currency: 'BRL',
      num_items: numItems,
      content_type: 'product',
    };

    (window as any).fbq('track', 'InitiateCheckout', data);
  }

  /**
   * Rastreia adição de informações de pagamento
   */
  trackAddPaymentInfo(
    paymentMethod: string,
    planName: string,
    value: number
  ): void {
    if (!this.isPixelLoaded()) return;

    const data: FacebookPixelData = {
      payment_method: paymentMethod,
      plan_name: planName,
      value,
      currency: 'BRL',
    };

    (window as any).fbq('track', 'AddPaymentInfo', data);
  }

  /**
   * Rastreia compra/assinatura realizada
   */
  trackPurchase(
    contentName: string,
    transactionId: string,
    value: number,
    planId?: string,
    paymentMethod: string = 'pix'
  ): void {
    if (!this.isPixelLoaded()) return;

    const data: FacebookPixelData = {
      content_name: contentName,
      content_ids: planId ? [planId] : [],
      content_type: 'product',
      value,
      currency: 'BRL',
      transaction_id: transactionId,
      num_items: 1,
      payment_method: paymentMethod,
    };

    (window as any).fbq('track', 'Purchase', data);
  }

  /**
   * Rastreia adição à wishlist/favoritos
   */
  trackAddToWishlist(contentName: string, contentIds: string[]): void {
    if (!this.isPixelLoaded()) return;

    const data: FacebookPixelData = {
      content_name: contentName,
      content_ids: contentIds,
      content_type: 'product',
      value: 0,
      currency: 'BRL',
    };

    (window as any).fbq('track', 'AddToWishlist', data);
  }

  /**
   * Rastreia remoção da wishlist
   */
  trackRemoveFromWishlist(contentName: string, contentIds: string[]): void {
    if (!this.isPixelLoaded()) return;

    const data: FacebookPixelData = {
      content_name: contentName,
      content_ids: contentIds,
    };

    (window as any).fbq('track', 'RemoveFromWishlist', data);
  }

  /**
   * Rastreia busca de conteúdo
   */
  trackSearch(searchString: string, contentCategory: string = 'Series'): void {
    if (!this.isPixelLoaded()) return;

    const data: FacebookPixelData = {
      search_string: searchString,
      content_category: contentCategory,
    };

    (window as any).fbq('track', 'Search', data);
  }

  /**
   * Rastreia conclusão de registro
   */
  trackCompleteRegistration(
    email: string,
    status: 'success' | 'failed' = 'success',
    firstName?: string,
    lastName?: string
  ): void {
    if (!this.isPixelLoaded()) return;

    const data: FacebookPixelData = {
      status,
    };

    // Hash do email para privacidade
    if (email) {
      data.em = this.hashEmail(email);
    }

    if (firstName) data.first_name = firstName;
    if (lastName) data.last_name = lastName;

    (window as any).fbq('track', 'CompleteRegistration', data);
  }

  /**
   * Rastreia evento customizado - PIX Gerado
   */
  trackPixGenerated(
    planName: string,
    orderId: string,
    expirationTime: string,
    value: number
  ): void {
    if (!this.isPixelLoaded()) return;

    const data: FacebookPixelData = {
      plan_name: planName,
      order_id: orderId,
      expiration_time: expirationTime,
      value,
      currency: 'BRL',
    };

    (window as any).fbq('trackCustom', 'PixGenerated', data);
  }

  /**
   * Rastreia início de reprodução de vídeo
   */
  trackVideoPlay(videoName: string, durationSeconds: number): void {
    if (!this.isPixelLoaded()) return;

    const data: FacebookPixelData = {
      video_name: videoName,
      duration_seconds: durationSeconds,
      content_category: 'Video',
    };

    (window as any).fbq('trackCustom', 'VideoPlay', data);
  }

  /**
   * Rastreia conclusão de vídeo (90% assistido)
   */
  trackVideoComplete(videoName: string): void {
    if (!this.isPixelLoaded()) return;

    const data: FacebookPixelData = {
      video_name: videoName,
      content_category: 'Video',
    };

    (window as any).fbq('trackCustom', 'VideoComplete', data);
  }

  /**
   * Rastreia contato/suporte
   */
  trackContact(contactType: string = 'support'): void {
    if (!this.isPixelLoaded()) return;

    const data: FacebookPixelData = {
      contact_type: contactType,
    };

    (window as any).fbq('track', 'Contact', data);
  }

  /**
   * Rastreia evento de erro
   */
  trackError(
    errorType: string,
    errorMessage: string,
    page: string
  ): void {
    if (!this.isPixelLoaded()) return;

    const data: FacebookPixelData = {
      error_type: errorType,
      error_message: errorMessage,
      page,
    };

    (window as any).fbq('trackCustom', 'Error', data);
  }

  /**
   * Rastreia evento customizado
   */
  trackCustomEvent(eventName: string, data: FacebookPixelData): void {
    if (!this.isPixelLoaded()) return;

    (window as any).fbq('trackCustom', eventName, data);
  }

  /**
   * Verifica se Pixel está carregado
   */
  private isPixelLoaded(): boolean {
    return typeof window !== 'undefined' && (window as any).fbq !== undefined;
  }

  /**
   * Hash SHA-256 simples do email para privacidade (Facebook SHA-256 normalization)
   */
  private hashEmail(email: string): string {
    // Implementação simplificada - em produção, usar biblioteca crypto
    const normalizedEmail = email.toLowerCase().trim();

    // Se não tiver crypto disponível, retornar email como está
    if (typeof window !== 'undefined' && (window as any).crypto) {
      return normalizedEmail;
    }

    return normalizedEmail;
  }
}

// Exporta singleton
export const facebookPixel = new FacebookPixelService();
